import React from 'react';
import { ArrowLeft, Clock, Trophy, HelpCircle } from 'lucide-react';
import { useApp } from './AppContext';
import { YandexRTB } from './YandexRTB';
import type { Screen } from '../App';

interface ExamScreenProps {
  onNavigate: (screen: Screen) => void;
  onBack: () => void;
}

export const ExamScreen: React.FC<ExamScreenProps> = ({ onNavigate, onBack }) => {
  const { questions, startTest, testSession, getBackgroundClass } = useApp();

  const startExam = () => {
    const shuffled = [...questions].sort(() => Math.random() - 0.5);
    const examQuestions = shuffled.slice(0, 10);
    startTest('exam', examQuestions);
    onNavigate('test');
  };

  const continueExam = () => {
    if (testSession) {
      onNavigate('test');
    }
  };

  // Check if there's an interrupted exam session
  const hasInterruptedExam = testSession && !testSession.isCompleted && testSession.type === 'exam';

  return (
    <div className={`screen-min-h-full ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col`}>
      {/* Header */}
      <div className="app-header-fixed flex items-center">
        <button
          onClick={onBack}
          className="mr-2 sm:mr-3 arrow-button"
          aria-label="Назад"
        >
          <ArrowLeft className="arrow-icon" />
        </button>
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <Trophy className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0" />
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl landscape-compact-text-xs landscape:text-sm font-semibold glass-text truncate">Экзамен</h1>
        </div>

        <button
          onClick={() => onNavigate('exam-help')}
          className="ml-2 sm:ml-3 arrow-button flex-shrink-0"
          style={{ transition: 'none' }}
          aria-label="Помощь"
        >
          <HelpCircle className="arrow-icon" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="app-content-scrollable">

        {/* Exam Info Card */}
        <div className="mb-4 sm:mb-6 md:mb-8 glass-panel rounded-xl">
          <div className="p-3 sm:p-4 md:p-6">

            <div className="space-y-3 sm:space-y-4 md:space-y-6">
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 text-center">
                <div className="p-2 sm:p-3 md:p-4 glass-panel rounded-lg">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-blue-200">10</div>
                  <div className="text-xs sm:text-sm md:text-base glass-text-muted">Вопросов</div>
                </div>
                <div className="p-2 sm:p-3 md:p-4 glass-panel rounded-lg">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-orange-200">15</div>
                  <div className="text-xs sm:text-sm md:text-base glass-text-muted">Минут</div>
                </div>
              </div>

              <div className="p-3 sm:p-4 md:p-5 glass-panel rounded-lg text-center">
                <p className="text-xs sm:text-sm md:text-base glass-text-secondary">
                  Для успешной сдачи экзамена необходимо допустить не более 1 ошибки
                </p>
              </div>
            </div>
          </div>
        </div>



        {/* Continue Interrupted Exam Button */}
        {hasInterruptedExam && (
          <button
            onClick={continueExam}
            className="w-full mb-4 sm:mb-6 md:mb-8 glass-panel rounded-2xl glass-interactive shadow-lg"
            style={{
              transition: 'none',
              padding: '1rem 1.5rem'
            }}
          >
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 glass-icon" />
              <span className="glass-text text-sm sm:text-base md:text-lg font-medium">
                Продолжить прерванный экзамен
              </span>
            </div>
          </button>
        )}

        {/* Start Exam Button */}
        <button
          onClick={startExam}
          className="w-full mb-4 sm:mb-6 md:mb-8 glass-panel rounded-2xl glass-interactive shadow-lg relative overflow-hidden group hover:scale-[1.01] transition-transform"
          style={{
            padding: '1.5rem',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
          <div className="flex items-center justify-center gap-2 sm:gap-3 relative z-10">
            <Trophy className="h-6 w-6 sm:h-7 sm:w-7 glass-icon" />
            <span className="glass-text text-base sm:text-lg md:text-xl font-semibold tracking-wide" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              {hasInterruptedExam ? 'Начать новый экзамен' : 'Начать экзамен'}
            </span>
          </div>
        </button>

        {/* Statistics Card */}
        <div className="mb-4 sm:mb-6 md:mb-8 glass-panel rounded-xl">
          <div className="p-3 sm:p-4 md:p-6 text-center">
            <h2 className="font-medium glass-text mb-2 sm:mb-3 text-sm sm:text-base md:text-lg">Готовность к экзамену</h2>
            <div className="text-xs sm:text-sm md:text-base glass-text-secondary mb-1 sm:mb-2 font-medium">
              Всего вопросов в базе: <span className="text-blue-300">{questions.length}</span>
            </div>
            <div className="text-xs sm:text-xs md:text-sm glass-text-muted">
              Рекомендуется пройти подготовку по всем категориям перед экзаменом.
            </div>
          </div>
        </div>

        {/* Нативный рекламный блок */}
        <div className="w-full mb-4 sm:mb-6 md:mb-8 flex justify-center rounded-xl bg-white/5 min-h-[100px]">
          <YandexRTB type="native" idMobile="R-A-17579918-7" idDesktop="R-A-17579918-7" />
        </div>
      </div>
    </div>
  );
};